/**
 * Shared types for Majors Guide feature
 * @module MajorsGuide/types
 */

import { Theme } from '@mui/material';

/**
 * Relevance rating levels used for filtering career-major relationships
 */
export type RelevanceRating = 'High' | 'Medium' | 'Low';

/**
 * Attribute level strings from source data
 * Maps to visual progress percentages and colors
 */
export type AttributeLevel =
  | 'Very Low'
  | 'Low'
  | 'Less Rigorous'
  | 'Moderate'
  | 'Rigorous'
  | 'High'
  | 'Very Rigorous'
  | 'Very High';

/**
 * Props for components that display level-based attributes
 */
export interface AttributeLevelConfig {
  /** Percentage fill for progress bar (0-100) */
  percentage: number;
  /** Color token name or hex value */
  color: 'gray' | 'yellow' | 'green';
}

/**
 * Mapping from level string to visual configuration
 */
export const LEVEL_CONFIG: Record<string, AttributeLevelConfig> = {
  'Very Low': { percentage: 10, color: 'gray' },
  Low: { percentage: 25, color: 'gray' },
  'Less Rigorous': { percentage: 33, color: 'gray' },
  Moderate: { percentage: 50, color: 'yellow' },
  Rigorous: { percentage: 66, color: 'green' },
  High: { percentage: 75, color: 'green' },
  'Very Rigorous': { percentage: 90, color: 'green' },
  'Very High': { percentage: 90, color: 'green' },
};

/**
 * Get relevance color from design tokens
 * Must be called within a theme-aware context
 */
export const getRelevanceColor = (rating: RelevanceRating, theme: Theme): string => {
  const colors = {
    High: theme.backrs.colors['Success/500'],
    Medium: theme.backrs.colors['Warning/400'],
    Low: theme.backrs.colors['Error/500'],
  };

  return colors[rating];
};

/**
 * Color values for relevance ratings
 * Hardcoded: Required for non-theme contexts (e.g., PDF generation, static exports)
 */
export const RELEVANCE_COLORS: Record<RelevanceRating, string> = {
  High: '#2CB669', // Success/500
  Medium: '#F7AE41', // Warning/400
  Low: '#F04438', // Error/500
};

// ============================================
// API Response Types - Relevance Endpoint
// ============================================

/**
 * Single ranked result item from /api/majors/guide/relevance
 * Server computes score and includes ratings/rationales per selection
 */
export interface RelevanceResultItem {
  /** Unique identifier for the major or career path */
  id: number;
  /** Code/slug for the major or career path */
  code: string;
  /** Name of the major or career path */
  name: string;
  /** Computed relevance score (higher = more relevant) */
  score: number;
  /** Rating per selection: selection name -> rating level */
  ratings: Record<string, RelevanceRating>;
  /** Rationale text per selection: selection name -> explanation */
  rationales: Record<string, string>;
}

/**
 * Mode for relevance explorer - determines what items are being selected/displayed
 */
export type RelevanceMode = 'majors' | 'paths';

/**
 * Response from GET /api/majors/guide/relevance
 * Paginated, server-ranked results
 */
export interface RelevanceResponse {
  items: RelevanceResultItem[];
  total: number;
  page: number;
  perPage: number;
  meta: {
    mode: RelevanceMode;
    selections: string[];
    primary: string | null;
    levels: RelevanceRating[];
  };
}

// ============================================
// API Response Types - Items Endpoint
// ============================================

/**
 * Item from /api/majors/guide/items for selection UI
 */
export interface SelectableItem {
  /** Display name of the item */
  name: string;
  /** Group/category for organization (e.g., college name, career category) */
  group: string;
}

/**
 * Response from GET /api/majors/guide/items
 */
export interface ItemsResponse {
  items: SelectableItem[];
}

// ============================================
// API Response Types - Explore Filters Endpoint
// ============================================

/**
 * Generic filter option for dropdown/select components
 * Used by both collegeTypes and sortValues arrays
 */
export interface FilterOption {
  /** Display text for dropdown */
  label: string;
  /** Value to send as filter/sort parameter */
  value: string;
}

/**
 * Response from GET /api/majors/guide/explore/filters
 * Consolidates all filter metadata for the explore page
 */
export interface ExploreFiltersResponse {
  /** Options for college type filter dropdown (sorted alphabetically by backend) */
  collegeTypes: FilterOption[];
  /** Options for sort dropdown (order is intentional: Name first, then metrics) */
  sortValues: FilterOption[];
}

// ============================================
// API Response Types - Browse Endpoint
// ============================================

/**
 * Single major from /api/majors/guide/browse
 */
export interface MajorBrowseItem {
  id: number;
  name: string;
  code: string;
  description: string;
  typicalCollege: string;
  demand: string;
  underEmployment: number;
  competitiveness: string;
  workload: string;
  optionValueName: string;
  gradSchoolNecessity: string;
  rigor: number;
  media_url?: string;
}

/**
 * Response from GET /api/majors/guide/browse
 * Paginated majors with filtering/sorting applied server-side
 */
export interface MajorsBrowseResponse {
  items: MajorBrowseItem[];
  total: number;
  page: number;
  perPage: number;
}

/**
 * A major reduced to its id, name, and college.
 */
export interface MajorCollegeSummary {
  id: number;
  name: string;
  typicalCollege: string;
}

/**
 * Response from GET /majors/guide/overview: every major reduced to its
 * id/name/college, plus the distinct college types.
 */
export interface MajorsGuideOverviewResponse {
  majors: MajorCollegeSummary[];
  collegeTypes: FilterOption[];
}

// ============================================
// Component Props Types
// ============================================

/**
 * Props for MultiSelectSearchList component
 */
export interface MultiSelectSearchListProps {
  /** Available items to select from */
  items: SelectableItem[];
  /** Currently selected item names */
  selectedItems: string[];
  /** Callback when selection changes */
  onSelectionChange: (items: string[]) => void;
  /** Placeholder text for search input (default: "Search...") */
  placeholder?: string;
  /** Optional maximum number of selections allowed */
  maxSelections?: number;
  /** Message shown when no items match search */
  emptyMessage?: string;
  /** Optional: custom render function for items */
  renderItem?: (item: SelectableItem, isSelected: boolean) => React.ReactNode;
}

/**
 * Props for RankedResultsList component
 */
export interface RankedResultsListProps {
  /** Ranked result items to display */
  results: RelevanceResultItem[];
  /** Current mode - affects labels */
  mode: RelevanceMode;
  /** Original selections for context display */
  selections: string[];
  /** Optional: callback when an item is clicked */
  onItemClick?: (item: RelevanceResultItem) => void;
  /** Whether results are loading */
  loading?: boolean;
  /** Message shown when no results */
  emptyMessage?: string;
}

/**
 * Filter state for Search Majors page
 */
export interface SearchMajorsFilters {
  search: string;
  colleges: string[];
  sort: 'name' | 'underemployment' | 'demand' | 'rigor' | 'optionValue' | 'relevance';
  order: 'asc' | 'desc';
  page: number;
}

// ============================================
// API Response Types - Major Career Matrix
// ============================================

/**
 * Single cell in the major-career relevance matrix
 */
export interface MatrixCell {
  majorId: number;
  careerPathId: number;
  relevance: RelevanceRating;
  rationale: string;
}

/**
 * Major info for matrix
 */
export interface MatrixMajor {
  id: number;
  name: string;
  code: string;
}

/**
 * Career path info for matrix
 */
export interface MatrixCareerPath {
  id: number;
  name: string;
}

/**
 * Response from GET /api/major-career-matrix
 */
export interface MajorCareerMatrixResponse {
  majors: MatrixMajor[];
  careerPaths: MatrixCareerPath[];
  matrix: MatrixCell[];
}
